import { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, X, Download, Paperclip, ExternalLink, FileText, AlertTriangle, Fish, Monitor } from "lucide-react";
import { api, API, formatApiErrorDetail } from "@/lib/api";
import { Switch } from "@/components/ui/switch";

const CATEGORIES = [
  "Tanks", "Racks/Build", "Livestock/Fish", "Equipment/Filtration", "Food",
  "Plumbing", "Electrics", "Plants", "Heating", "Water Treatment/Test Kits",
  "Postage/Packaging", "Travel/Fuel", "Subsistence", "Vehicle Insurance & Repairs", "Software/Subscriptions", "Use of Home", "Misc",
];
const INCOME_CATEGORIES = [
  "Fish Sales", "Equipment Sales", "Uber/Just Eat Contracting",
  "Sponsorship", "YouTube/AdSense", "Other",
];
const PAYMENT_METHODS = ["Card", "Cash", "Bank Transfer", "PayPal", "Other"];

const gbp = (n) => "£" + Number(n || 0).toLocaleString("en-GB", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const today = () => new Date().toISOString().slice(0, 10);
const inputCls =
  "w-full bg-[#0C131D] border border-white/10 rounded-lg px-3.5 py-2.5 text-sm text-white placeholder:text-brand-textMuted focus:outline-none focus:ring-2 focus:ring-brand-cyan";

const receiptUrl = (fid) => `/r/${fid}`;
const csvUrl = (which, ty) => `${API}/finance/export/${which}.csv?auth=${localStorage.getItem("fhp_token")}${ty && ty !== "all" ? `&tax_year=${ty}` : ""}&base=${encodeURIComponent(window.location.origin)}`;
const pdfUrl = (ty) => `${API}/finance/export/summary.pdf?auth=${localStorage.getItem("fhp_token")}${ty && ty !== "all" ? `&tax_year=${ty}` : ""}&base=${encodeURIComponent(window.location.origin)}`;

// UK tax year runs 6 Apr YYYY → 5 Apr YYYY+1. Returns the starting year.
const currentTaxYear = () => {
  const d = new Date();
  const afterApr6 = d.getMonth() > 3 || (d.getMonth() === 3 && d.getDate() >= 6);
  return afterApr6 ? d.getFullYear() : d.getFullYear() - 1;
};
const taxYearOptions = () => {
  const cur = currentTaxYear();
  const arr = [];
  for (let y = cur; y >= 2023; y--) arr.push(y);
  return arr;
};
const taxYearLabel = (y) => `${y}/${String(y + 1).slice(2)}`;
const monthLabel = (m) => {
  const [y, mo] = m.split("-");
  return new Date(Number(y), Number(mo) - 1, 1).toLocaleDateString("en-GB", { month: "short", year: "numeric" });
};
const FUNDERS = [
  { value: "owner", label: "Owner (personal)" },
  { value: "company", label: "Company" },
];
const BILLING_CYCLES = ["Monthly", "Annual", "One-off"];

const StatCard = ({ label, value, accent, onClick, active }) => {
  const Tag = onClick ? "button" : "div";
  return (
    <Tag onClick={onClick} className={`text-left bg-[#0C131D] border rounded-xl p-5 ${active ? "border-brand-cyan" : "border-white/10"} ${onClick ? "cursor-pointer hover:border-brand-cyan/60 transition-colors" : ""}`} data-testid={`stat-${label.toLowerCase().replace(/\s+/g, "-")}`}>
      <p className="text-brand-textMuted text-xs uppercase tracking-wider">{label}</p>
      <p className={`mt-2 font-display font-bold text-2xl ${accent || "text-white"}`}>{value}</p>
      {onClick && <p className="mt-1 text-brand-textMuted text-xs">{active ? "Showing below" : "Click to view"}</p>}
    </Tag>
  );
};

const ReceiptNotRequired = ({ form, setForm, testid }) => (
  <label className="flex items-center gap-2 mt-3 cursor-pointer select-none">
    <input type="checkbox" checked={!!form.receipt_not_required} onChange={(e) => setForm((f) => ({ ...f, receipt_not_required: e.target.checked }))} className="h-4 w-4 accent-[#25C4D8]" data-testid={testid} />
    <span className="text-sm text-brand-textSec">Receipt not required for this expense (treat as receipted)</span>
  </label>
);

const CURRENCIES = ["GBP", "USD", "EUR", "HUF"];
const CUR_SYMBOL = { GBP: "£", USD: "$", EUR: "€", HUF: "Ft " };
const OrigHint = ({ t }) =>
  t.currency && t.currency !== "GBP" && t.original_amount ? (
    <div className="text-brand-textMuted text-xs font-normal">{CUR_SYMBOL[t.currency] || ""}{Number(t.original_amount).toFixed(2)} {t.currency}</div>
  ) : null;

const AmountField = ({ form, setForm, testid = "tx" }) => {
  const currency = form.currency || "GBP";
  const [loadingRate, setLoadingRate] = useState(false);

  const changeCurrency = async (cur) => {
    if (cur === "GBP") {
      setForm((f) => ({ ...f, currency: "GBP", original_amount: 0, fx_rate: 0 }));
      return;
    }
    setLoadingRate(true);
    try {
      const { data } = await api.get("/finance/fx", { params: { base: cur } });
      setForm((f) => {
        const orig = parseFloat(f.original_amount) || 0;
        return { ...f, currency: cur, fx_rate: data.rate, amount: orig ? (orig * data.rate).toFixed(2) : f.amount };
      });
      toast.success(`1 ${cur} = £${data.rate} (${data.date})`);
    } catch {
      toast.error("Couldn't fetch today's rate — switch to GBP to enter £ directly.");
    } finally {
      setLoadingRate(false);
    }
  };

  const changeOriginal = (e) => {
    const orig = e.target.value;
    setForm((f) => ({ ...f, original_amount: orig, amount: ((parseFloat(orig) || 0) * (parseFloat(f.fx_rate) || 0)).toFixed(2) }));
  };

  return (
    <div>
      <label className="text-sm text-brand-textSec mb-1.5 block">Amount</label>
      <div className="flex gap-2">
        <select value={currency} onChange={(e) => changeCurrency(e.target.value)} className={`${inputCls} !w-24 shrink-0`} data-testid={`${testid}-currency`}>
          {CURRENCIES.map((c) => <option key={c} value={c} className="bg-[#0C131D]">{c}</option>)}
        </select>
        {currency === "GBP" ? (
          <input type="number" step="0.01" required value={form.amount} onChange={(e) => setForm((f) => ({ ...f, amount: e.target.value }))} className={inputCls} data-testid={`${testid}-amount`} placeholder="0.00" />
        ) : (
          <input type="number" step="0.01" required value={form.original_amount} onChange={changeOriginal} className={inputCls} data-testid={`${testid}-original-amount`} placeholder={`0.00 ${currency}`} />
        )}
      </div>
      {currency !== "GBP" && (
        <p className="text-brand-textMuted text-xs mt-1.5" data-testid={`${testid}-fx-note`}>
          {loadingRate ? "Fetching today's rate…" : form.fx_rate ? `≈ ${gbp(form.amount)} at 1 ${currency} = £${form.fx_rate} (today's rate). Totals use the £ value.` : "Enter the amount to convert to £."}
        </p>
      )}
    </div>
  );
};

// ---- Transaction modal ----
const TxModal = ({ initial, preset, onClose, onSaved }) => {
  const [form, setForm] = useState(
    initial || { type: preset?.type || "expense", gig: !!preset?.gig, amount: "", date: "", category: preset?.category || "", description: "", vendor: "", payment_method: "", funded_by: "owner", receipt_file_id: "", receipt_filename: "", receipt_not_required: false, currency: "GBP", original_amount: "", fx_rate: 0 }
  );
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const uploadReceipt = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/finance/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setForm((f) => ({ ...f, receipt_file_id: data.file_id, receipt_filename: data.filename }));
      toast.success("Receipt attached");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Upload failed");
    } finally {
      setUploading(false);
    }
  };

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = { ...form, amount: parseFloat(form.amount) || 0, original_amount: parseFloat(form.original_amount) || 0, fx_rate: parseFloat(form.fx_rate) || 0 };
      if (initial?.id) await api.put(`/finance/transactions/${initial.id}`, payload);
      else await api.post("/finance/transactions", payload);
      toast.success("Saved");
      onSaved();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm grid place-items-center p-4 overflow-y-auto" data-testid="tx-modal">
      <div className="bg-[#0C131D] border border-white/10 rounded-xl w-full max-w-lg my-8">
        <div className="flex items-center justify-between px-6 py-4 border-b border-white/10">
          <h3 className="font-display font-semibold text-white">{initial?.id ? "Edit" : "New"} entry</h3>
          <button onClick={onClose} className="text-brand-textMuted hover:text-white" data-testid="tx-modal-close"><X size={20} /></button>
        </div>
        <form onSubmit={save} className="p-6 space-y-4">
          <div className="grid grid-cols-2 gap-2 p-1 bg-[#131C2A] rounded-lg">
            {["expense", "income"].map((t) => (
              <button key={t} type="button" onClick={() => setForm((f) => ({ ...f, type: t }))} data-testid={`tx-type-${t}`}
                className={`py-2 rounded-md text-sm font-medium capitalize transition-colors ${form.type === t ? (t === "expense" ? "bg-red-500/80 text-white" : "bg-brand-cyan text-brand-base") : "text-brand-textSec"}`}>
                {t}
              </button>
            ))}
          </div>
          <div className="grid sm:grid-cols-2 gap-4">
            <AmountField form={form} setForm={setForm} testid="tx" />
            <div>
              <label className="text-sm text-brand-textSec mb-1.5 block">Date</label>
              <input type="date" required value={form.date} onChange={set("date")} className={inputCls} data-testid="tx-date" />
            </div>
          </div>
          <div>
            <label className="text-sm text-brand-textSec mb-1.5 block">Category</label>
            <select value={form.category} onChange={set("category")} className={inputCls} data-testid="tx-category">
              <option value="">—</option>
              {(form.type === "income" ? INCOME_CATEGORIES : CATEGORIES).map((c) => <option key={c} value={c} className="bg-[#0C131D]">{c}</option>)}
            </select>
          </div>
          <div>
            <label className="text-sm text-brand-textSec mb-1.5 block">Reason / description</label>
            <input value={form.description} onChange={set("description")} className={inputCls} data-testid="tx-description" placeholder="What was it for?" />
          </div>
          {form.type === "expense" && (
            <div>
              <label className="text-sm text-brand-textSec mb-1.5 block">Funded by</label>
              <div className="grid grid-cols-2 gap-2 p-1 bg-[#131C2A] rounded-lg">
                {FUNDERS.map((f) => (
                  <button key={f.value} type="button" onClick={() => setForm((s) => ({ ...s, funded_by: f.value }))} data-testid={`tx-funded-${f.value}`}
                    className={`py-2 rounded-md text-sm font-medium transition-colors ${form.funded_by === f.value ? "bg-brand-cyan text-brand-base" : "text-brand-textSec"}`}>
                    {f.label}
                  </button>
                ))}
              </div>
              <p className="text-brand-textMuted text-xs mt-1.5">"Owner" means you paid from personal money — it's added to what the company owes you.</p>
            </div>
          )}
          <div className="grid sm:grid-cols-2 gap-4">
            <div>
              <label className="text-sm text-brand-textSec mb-1.5 block">Vendor / source</label>
              <input value={form.vendor} onChange={set("vendor")} className={inputCls} data-testid="tx-vendor" placeholder="Where from / to" />
            </div>
            <div>
              <label className="text-sm text-brand-textSec mb-1.5 block">Payment method</label>
              <select value={form.payment_method} onChange={set("payment_method")} className={inputCls} data-testid="tx-payment">
                <option value="">—</option>
                {PAYMENT_METHODS.map((p) => <option key={p} value={p} className="bg-[#0C131D]">{p}</option>)}
              </select>
            </div>
          </div>
          <label className="flex items-center gap-2 cursor-pointer select-none">
            <input type="checkbox" checked={!!form.gig} onChange={(e) => setForm((f) => ({ ...f, gig: e.target.checked }))} className="h-4 w-4 accent-[#25C4D8]" data-testid="tx-gig" />
            <span className="text-sm text-brand-textSec">Uber / Just Eat work</span>
          </label>
          <div>
            <label className="text-sm text-brand-textSec mb-1.5 block">Receipt (image or PDF)</label>
            <div className="flex items-center gap-3">
              <label className="btn-ghost px-4 py-2 text-sm inline-flex items-center gap-2 cursor-pointer">
                <Paperclip size={15} /> {uploading ? "Uploading…" : form.receipt_file_id ? "Replace" : "Attach"}
                <input type="file" accept="image/*,application/pdf" onChange={uploadReceipt} className="hidden" data-testid="tx-receipt-input" />
              </label>
              {form.receipt_file_id && (
                <>
                  <a href={receiptUrl(form.receipt_file_id)} target="_blank" rel="noreferrer" className="text-brand-cyan text-sm inline-flex items-center gap-1" data-testid="tx-receipt-view">
                    View <ExternalLink size={13} />
                  </a>
                  <button type="button" onClick={() => setForm((f) => ({ ...f, receipt_file_id: "", receipt_filename: "" }))} className="text-brand-textMuted hover:text-red-400 text-sm">Remove</button>
                </>
              )}
            </div>
            {form.type === "expense" && !form.receipt_file_id && <ReceiptNotRequired form={form} setForm={setForm} testid="tx-receipt-not-required" />}
          </div>
          <div className="flex gap-3 pt-1">
            <button type="submit" disabled={saving} className="btn-primary px-5 py-2.5 text-sm disabled:opacity-60" data-testid="tx-save">{saving ? "Saving…" : "Save"}</button>
            <button type="button" onClick={onClose} className="btn-ghost px-5 py-2.5 text-sm">Cancel</button>
          </div>
        </form>
      </div>
    </div>
  );
};

// ---- Mileage modal (postcode-based auto distance) ----
const MileageModal = ({ initial, preset, onClose, onSaved }) => {
  const [form, setForm] = useState(
    initial
      ? { date: initial.date || "", from_postcode: initial.from_postcode || "", to_postcode: initial.to_postcode || "", is_return: !!initial.is_return, purpose: initial.purpose || "", gig: !!initial.gig }
      : { date: "", from_postcode: "", to_postcode: "", is_return: false, purpose: "", gig: !!preset?.gig }
  );
  const [saving, setSaving] = useState(false);
  const [est, setEst] = useState(null);
  const [estErr, setEstErr] = useState("");
  const [estimating, setEstimating] = useState(false);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const pcLen = (p) => (p || "").replace(/\s/g, "").length;

  useEffect(() => {
    const fp = (form.from_postcode || "").trim();
    const tp = (form.to_postcode || "").trim();
    setEst(null); setEstErr("");
    if (pcLen(fp) < 5 || pcLen(tp) < 5) return;
    let cancelled = false;
    setEstimating(true);
    const t = setTimeout(() => {
      api.get("/finance/mileage/estimate", { params: { from_postcode: fp, to_postcode: tp, is_return: form.is_return } })
        .then((r) => { if (!cancelled) setEst(r.data); })
        .catch((err) => { if (!cancelled) setEstErr(formatApiErrorDetail(err.response?.data?.detail) || "Couldn't calculate distance."); })
        .finally(() => { if (!cancelled) setEstimating(false); });
    }, 700);
    return () => { cancelled = true; clearTimeout(t); };
  }, [form.from_postcode, form.to_postcode, form.is_return]);

  const save = async (e) => {
    e.preventDefault();
    if (estErr) { toast.error("Please fix the postcodes before saving."); return; }
    if (!est) { toast.error("Enter both postcodes so the mileage can be calculated."); return; }
    setSaving(true);
    try {
      const payload = { date: form.date, from_postcode: form.from_postcode.trim(), to_postcode: form.to_postcode.trim(), is_return: !!form.is_return, purpose: (form.purpose || "").trim(), gig: !!form.gig };
      if (initial?.id) await api.put(`/finance/mileage/${initial.id}`, payload);
      else await api.post("/finance/mileage", payload);
      toast.success("Saved");
      onSaved();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm grid place-items-center p-4 overflow-y-auto" data-testid="mileage-modal">
      <div className="bg-[#0C131D] border border-white/10 rounded-xl w-full max-w-lg my-8">
        <div className="flex items-center justify-between px-6 py-4 border-b border-white/10">
          <h3 className="font-display font-semibold text-white">{initial?.id ? "Edit" : "New"} mileage</h3>
          <button onClick={onClose} className="text-brand-textMuted hover:text-white" data-testid="mileage-modal-close"><X size={20} /></button>
        </div>
        <form onSubmit={save} className="p-6 space-y-4">
          <div>
            <label className="text-sm text-brand-textSec mb-1.5 block">Date</label>
            <input type="date" required value={form.date} onChange={set("date")} className={inputCls} data-testid="mileage-date" />
          </div>
          <div className="grid sm:grid-cols-2 gap-4">
            <div>
              <label className="text-sm text-brand-textSec mb-1.5 block">Starting postcode</label>
              <input value={form.from_postcode} onChange={set("from_postcode")} className={inputCls} data-testid="mileage-from-postcode" placeholder="e.g. SW1A 1AA" autoCapitalize="characters" />
            </div>
            <div>
              <label className="text-sm text-brand-textSec mb-1.5 block">Ending postcode</label>
              <input value={form.to_postcode} onChange={set("to_postcode")} className={inputCls} data-testid="mileage-to-postcode" placeholder="e.g. M16 0RA" autoCapitalize="characters" />
            </div>
          </div>
          <div className="flex items-center justify-between gap-4 bg-[#0A0F16] border border-white/10 rounded-lg px-4 py-3">
            <div>
              <p className="text-sm text-white font-medium">Return trip</p>
              <p className="text-xs text-brand-textMuted mt-0.5">On = there and back (miles doubled).</p>
            </div>
            <Switch checked={form.is_return} onCheckedChange={(v) => setForm((f) => ({ ...f, is_return: v }))}
              className="data-[state=checked]:bg-brand-cyan data-[state=unchecked]:bg-white/15" data-testid="mileage-return-toggle" />
          </div>
          <div>
            <label className="text-sm text-brand-textSec mb-1.5 block">Reason for trip</label>
            <input value={form.purpose} onChange={set("purpose")} className={inputCls} data-testid="mileage-purpose" placeholder="e.g. Livestock pickup, supplier run" />
          </div>
          <label className="flex items-center gap-2 cursor-pointer select-none">
            <input type="checkbox" checked={!!form.gig} onChange={(e) => setForm((f) => ({ ...f, gig: e.target.checked }))} className="h-4 w-4 accent-[#25C4D8]" data-testid="mileage-gig" />
            <span className="text-sm text-brand-textSec">Uber / Just Eat work</span>
          </label>
          <div className="rounded-lg border border-white/10 bg-[#0A0F16] px-4 py-3 text-sm" data-testid="mileage-estimate">
            {estimating && <span className="text-brand-textMuted">Calculating driving distance…</span>}
            {!estimating && estErr && <span className="text-red-400">{estErr}</span>}
            {!estimating && !estErr && est && (
              <span className="text-brand-textSec">
                Distance: <span className="text-white font-semibold">{est.miles} miles</span>{est.is_return ? " (return)" : ""} ·
                Claim ≈ <span className="text-brand-cyan font-semibold">{gbp(est.claim_estimate)}</span>
                <span className="text-brand-textMuted text-xs block mt-1">HMRC 55p/mile (first 10,000 business miles, 25p after — tiering applied across the year).</span>
              </span>
            )}
            {!estimating && !estErr && !est && <span className="text-brand-textMuted">Enter both UK postcodes to auto-calculate the mileage.</span>}
          </div>
          <div className="flex gap-3 pt-1">
            <button type="submit" disabled={saving || !est || !!estErr} className="btn-primary px-5 py-2.5 text-sm disabled:opacity-50" data-testid="mileage-save">{saving ? "Saving…" : "Save"}</button>
            <button type="button" onClick={onClose} className="btn-ghost px-5 py-2.5 text-sm">Cancel</button>
          </div>
        </form>
      </div>
    </div>
  );
};

// ---- Live Fish modal ----
const LiveFishModal = ({ initial, onClose, onSaved }) => {
  const [form, setForm] = useState(
    initial || { date: "", species: "", quantity: "", amount: "", vendor: "", funded_by: "owner", receipt_file_id: "", receipt_filename: "", receipt_not_required: false, currency: "GBP", original_amount: "", fx_rate: 0 }
  );
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const uploadReceipt = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/finance/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setForm((f) => ({ ...f, receipt_file_id: data.file_id, receipt_filename: data.filename }));
      toast.success("Receipt attached");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Upload failed");
    } finally {
      setUploading(false);
    }
  };

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const qty = parseFloat(form.quantity) || 0;
      const payload = {
        ...form, type: "expense", category: "Livestock/Fish",
        amount: parseFloat(form.amount) || 0, quantity: qty,
        original_amount: parseFloat(form.original_amount) || 0, fx_rate: parseFloat(form.fx_rate) || 0,
        description: `${qty ? qty + " × " : ""}${form.species || "Live fish"}`.trim(),
      };
      if (initial?.id) await api.put(`/finance/transactions/${initial.id}`, payload);
      else await api.post("/finance/transactions", payload);
      toast.success("Saved");
      onSaved();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm grid place-items-center p-4 overflow-y-auto" data-testid="livefish-modal">
      <div className="bg-[#0C131D] border border-white/10 rounded-xl w-full max-w-lg my-8">
        <div className="flex items-center justify-between px-6 py-4 border-b border-white/10">
          <h3 className="font-display font-semibold text-white flex items-center gap-2"><Fish size={18} className="text-brand-cyan" /> {initial?.id ? "Edit" : "New"} live fish purchase</h3>
          <button onClick={onClose} className="text-brand-textMuted hover:text-white" data-testid="livefish-modal-close"><X size={20} /></button>
        </div>
        <form onSubmit={save} className="p-6 space-y-4">
          <div className="grid sm:grid-cols-2 gap-4">
            <div>
              <label className="text-sm text-brand-textSec mb-1.5 block">Date</label>
              <input type="date" required value={form.date} onChange={set("date")} className={inputCls} data-testid="livefish-date" />
            </div>
            <AmountField form={form} setForm={setForm} testid="livefish" />
          </div>
          <div className="grid sm:grid-cols-2 gap-4">
            <div>
              <label className="text-sm text-brand-textSec mb-1.5 block">Species</label>
              <input value={form.species} onChange={set("species")} className={inputCls} data-testid="livefish-species" placeholder="e.g. L134 Peppermint Pleco" />
            </div>
            <div>
              <label className="text-sm text-brand-textSec mb-1.5 block">Quantity</label>
              <input type="number" step="1" value={form.quantity} onChange={set("quantity")} className={inputCls} data-testid="livefish-quantity" placeholder="0" />
            </div>
          </div>
          <div>
            <label className="text-sm text-brand-textSec mb-1.5 block">Source / seller</label>
            <input value={form.vendor} onChange={set("vendor")} className={inputCls} data-testid="livefish-vendor" placeholder="Where from" />
          </div>
          <div>
            <label className="text-sm text-brand-textSec mb-1.5 block">Funded by</label>
            <div className="grid grid-cols-2 gap-2 p-1 bg-[#131C2A] rounded-lg">
              {FUNDERS.map((f) => (
                <button key={f.value} type="button" onClick={() => setForm((s) => ({ ...s, funded_by: f.value }))} data-testid={`livefish-funded-${f.value}`}
                  className={`py-2 rounded-md text-sm font-medium transition-colors ${form.funded_by === f.value ? "bg-brand-cyan text-brand-base" : "text-brand-textSec"}`}>
                  {f.label}
                </button>
              ))}
            </div>
          </div>
          <div>
            <label className="text-sm text-brand-textSec mb-1.5 block">Receipt (image or PDF)</label>
            <div className="flex items-center gap-3">
              <label className="btn-ghost px-4 py-2 text-sm inline-flex items-center gap-2 cursor-pointer">
                <Paperclip size={15} /> {uploading ? "Uploading…" : form.receipt_file_id ? "Replace" : "Attach"}
                <input type="file" accept="image/*,application/pdf" onChange={uploadReceipt} className="hidden" data-testid="livefish-receipt-input" />
              </label>
              {form.receipt_file_id && (
                <a href={receiptUrl(form.receipt_file_id)} target="_blank" rel="noreferrer" className="text-brand-cyan text-sm inline-flex items-center gap-1">
                  View <ExternalLink size={13} />
                </a>
              )}
            </div>
            {!form.receipt_file_id && <ReceiptNotRequired form={form} setForm={setForm} testid="livefish-receipt-not-required" />}
          </div>
          <div className="bg-brand-cyan/10 border border-brand-cyan/25 rounded-lg px-4 py-3 text-sm text-brand-textSec">
            Fish bought for the channel are a <span className="text-brand-cyan font-medium">100% allowable business cost</span> — the full amount is claimed. Keep the receipt and note the species/purpose.
          </div>
          <div className="flex gap-3 pt-1">
            <button type="submit" disabled={saving} className="btn-primary px-5 py-2.5 text-sm disabled:opacity-60" data-testid="livefish-save">{saving ? "Saving…" : "Save"}</button>
            <button type="button" onClick={onClose} className="btn-ghost px-5 py-2.5 text-sm">Cancel</button>
          </div>
        </form>
      </div>
    </div>
  );
};

// ---- Software / Subscriptions modal ----
const SoftwareModal = ({ initial, onClose, onSaved }) => {
  const [form, setForm] = useState(
    initial || { date: "", description: "", amount: "", billing_cycle: "Monthly", vendor: "", funded_by: "owner", receipt_file_id: "", receipt_filename: "", receipt_not_required: false, currency: "GBP", original_amount: "", fx_rate: 0 }
  );
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const uploadReceipt = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/finance/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setForm((f) => ({ ...f, receipt_file_id: data.file_id, receipt_filename: data.filename }));
      toast.success("Receipt attached");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Upload failed");
    } finally {
      setUploading(false);
    }
  };

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = { ...form, type: "expense", category: "Software/Subscriptions", amount: parseFloat(form.amount) || 0, original_amount: parseFloat(form.original_amount) || 0, fx_rate: parseFloat(form.fx_rate) || 0 };
      if (initial?.id) await api.put(`/finance/transactions/${initial.id}`, payload);
      else await api.post("/finance/transactions", payload);
      toast.success("Saved");
      onSaved();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm grid place-items-center p-4 overflow-y-auto" data-testid="software-modal">
      <div className="bg-[#0C131D] border border-white/10 rounded-xl w-full max-w-lg my-8">
        <div className="flex items-center justify-between px-6 py-4 border-b border-white/10">
          <h3 className="font-display font-semibold text-white flex items-center gap-2"><Monitor size={18} className="text-brand-cyan" /> {initial?.id ? "Edit" : "New"} software / subscription</h3>
          <button onClick={onClose} className="text-brand-textMuted hover:text-white" data-testid="software-modal-close"><X size={20} /></button>
        </div>
        <form onSubmit={save} className="p-6 space-y-4">
          <div>
            <label className="text-sm text-brand-textSec mb-1.5 block">Service / tool</label>
            <input required value={form.description} onChange={set("description")} className={inputCls} data-testid="software-name" placeholder="e.g. Claude Pro, Emergent, Adobe" />
          </div>
          <AmountField form={form} setForm={setForm} testid="software" />
          <div className="grid sm:grid-cols-2 gap-4">
            <div>
              <label className="text-sm text-brand-textSec mb-1.5 block">Billing</label>
              <select value={form.billing_cycle} onChange={set("billing_cycle")} className={inputCls} data-testid="software-cycle">
                {BILLING_CYCLES.map((c) => <option key={c} value={c} className="bg-[#0C131D]">{c}</option>)}
              </select>
            </div>
            <div>
              <label className="text-sm text-brand-textSec mb-1.5 block">Date</label>
              <input type="date" required value={form.date} onChange={set("date")} className={inputCls} data-testid="software-date" />
            </div>
          </div>
          <div>
            <label className="text-sm text-brand-textSec mb-1.5 block">Funded by</label>
            <div className="grid grid-cols-2 gap-2 p-1 bg-[#131C2A] rounded-lg">
              {FUNDERS.map((f) => (
                <button key={f.value} type="button" onClick={() => setForm((s) => ({ ...s, funded_by: f.value }))} data-testid={`software-funded-${f.value}`}
                  className={`py-2 rounded-md text-sm font-medium transition-colors ${form.funded_by === f.value ? "bg-brand-cyan text-brand-base" : "text-brand-textSec"}`}>
                  {f.label}
                </button>
              ))}
            </div>
          </div>
          <div>
            <label className="text-sm text-brand-textSec mb-1.5 block">Receipt / invoice (image or PDF)</label>
            <div className="flex items-center gap-3">
              <label className="btn-ghost px-4 py-2 text-sm inline-flex items-center gap-2 cursor-pointer">
                <Paperclip size={15} /> {uploading ? "Uploading…" : form.receipt_file_id ? "Replace" : "Attach"}
                <input type="file" accept="image/*,application/pdf" onChange={uploadReceipt} className="hidden" data-testid="software-receipt-input" />
              </label>
              {form.receipt_file_id && (
                <a href={receiptUrl(form.receipt_file_id)} target="_blank" rel="noreferrer" className="text-brand-cyan text-sm inline-flex items-center gap-1">
                  View <ExternalLink size={13} />
                </a>
              )}
            </div>
            {!form.receipt_file_id && <ReceiptNotRequired form={form} setForm={setForm} testid="software-receipt-not-required" />}
          </div>
          <div className="bg-brand-cyan/10 border border-brand-cyan/25 rounded-lg px-4 py-3 text-sm text-brand-textSec">
            Software &amp; subscriptions used to run the channel/business — editing tools, website hosting, AI assistants — are an <span className="text-brand-cyan font-medium">allowable business cost</span>. Keep the invoice/receipt.
          </div>
          <div className="flex gap-3 pt-1">
            <button type="submit" disabled={saving} className="btn-primary px-5 py-2.5 text-sm disabled:opacity-60" data-testid="software-save">{saving ? "Saving…" : "Save"}</button>
            <button type="button" onClick={onClose} className="btn-ghost px-5 py-2.5 text-sm">Cancel</button>
          </div>
        </form>
      </div>
    </div>
  );
};

// ---- Use of Home calculator ----
const numOr = (v) => parseFloat(v) || 0;
const BILL_FIELDS = [
  ["gas", "Gas (annual £)", null],
  ["electricity", "Electricity (annual £)", null],
  ["water", "Water (annual £)", null],
  ["mortgage_interest", "Mortgage interest (annual £)", "Interest only — not the capital repayment part."],
  ["council_tax", "Council tax (annual £)", null],
  ["broadband", "Broadband / phone (annual £)", null],
  ["other", "Other running costs (annual £)", null],
];

const UseOfHomePanel = ({ onSaved }) => {
  const [method, setMethod] = useState("actual");
  const [businessRooms, setBusinessRooms] = useState("1");
  const [totalRooms, setTotalRooms] = useState("");
  const [hours, setHours] = useState("");
  const [bills, setBills] = useState({});
  const [saving, setSaving] = useState(false);
  const setBill = (k) => (e) => setBills((b) => ({ ...b, [k]: e.target.value }));

  const roomProp = numOr(totalRooms) > 0 ? Math.min(numOr(businessRooms) / numOr(totalRooms), 1) : 0;
  const timeProp = numOr(hours) > 0 ? Math.min(numOr(hours) / 168, 1) : 1;
  const pct = roomProp * timeProp * 100;
  const billTotal = BILL_FIELDS.reduce((s, [k]) => s + numOr(bills[k]), 0);
  const actualClaim = billTotal * (pct / 100);
  const FLAT_ANNUAL = 6 * 52;
  const claim = method === "flat" ? FLAT_ANNUAL : actualClaim;

  const saveExpense = async () => {
    if (claim <= 0) { toast.error("Enter your figures first."); return; }
    setSaving(true);
    try {
      const description = method === "flat"
        ? "Use of home — HMRC flat rate £6/week (£312/yr)"
        : `Use of home — ${pct.toFixed(1)}% of household running costs`;
      await api.post("/finance/transactions", {
        type: "expense", category: "Use of Home", amount: Math.round(claim * 100) / 100,
        description, funded_by: "owner", date: today(),
      });
      toast.success("Saved as an expense");
      onSaved();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const labelCls = "text-sm text-brand-textSec mb-1.5 block";

  return (
    <div data-testid="use-of-home-panel" className="max-w-3xl">
      <div className="bg-amber-400/10 border border-amber-400/30 rounded-lg px-4 py-3 mb-6 text-sm text-amber-200/90">
        You run this through a <strong>limited company</strong>. Two common routes: a flat <strong>£6/week (£312/yr)</strong> working-from-home
        allowance with no paperwork, or a share of your actual household running costs under a rental/licence agreement between you and the company.
        This tool estimates the actual-cost route. <span className="text-amber-300">Guidance only — confirm eligibility and figures with your accountant.</span>
      </div>

      {/* method toggle */}
      <div className="inline-flex gap-2 p-1 bg-[#0C131D] rounded-full mb-6">
        {[["actual", "Actual costs"], ["flat", "Flat rate (£6/wk)"]].map(([v, l]) => (
          <button key={v} onClick={() => setMethod(v)} data-testid={`home-method-${v}`}
            className={`px-4 py-2 rounded-full text-sm font-medium transition-colors ${method === v ? "bg-brand-cyan text-brand-base" : "text-brand-textSec hover:text-white"}`}>
            {l}
          </button>
        ))}
      </div>

      {method === "flat" ? (
        <div className="bg-[#0C131D] border border-white/10 rounded-xl p-6">
          <p className="text-brand-textSec text-sm mb-2">HMRC's simplified working-from-home allowance for a director:</p>
          <p className="font-display font-bold text-3xl text-brand-cyan">{gbp(FLAT_ANNUAL)}<span className="text-brand-textMuted text-base font-normal"> / year</span></p>
          <p className="text-brand-textMuted text-xs mt-2">£6 per week × 52 weeks. No receipts or apportionment needed.</p>
        </div>
      ) : (
        <div className="grid lg:grid-cols-2 gap-6">
          {/* % helper */}
          <div className="bg-[#0C131D] border border-white/10 rounded-xl p-6">
            <h4 className="font-display font-semibold text-white mb-1">Work out your business-use %</h4>
            <p className="text-brand-textMuted text-xs mb-4">Count only proper rooms — exclude kitchens, bathrooms and hallways.</p>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className={labelCls}>Rooms used for business</label>
                <input type="number" step="1" value={businessRooms} onChange={(e) => setBusinessRooms(e.target.value)} className={inputCls} data-testid="home-business-rooms" placeholder="1" />
              </div>
              <div>
                <label className={labelCls}>Total rooms in home</label>
                <input type="number" step="1" value={totalRooms} onChange={(e) => setTotalRooms(e.target.value)} className={inputCls} data-testid="home-total-rooms" placeholder="e.g. 6" />
              </div>
            </div>
            <div className="mt-4">
              <label className={labelCls}>Hours per week used for business <span className="text-brand-textMuted">(optional)</span></label>
              <input type="number" step="1" value={hours} onChange={(e) => setHours(e.target.value)} className={inputCls} data-testid="home-hours" placeholder="Leave blank if the room is business-only" />
            </div>
            <div className="mt-5 pt-4 border-t border-white/10">
              <p className="text-brand-textSec text-sm">Estimated business use</p>
              <p className="font-display font-bold text-2xl text-brand-cyan" data-testid="home-pct">{pct.toFixed(1)}%</p>
              <p className="text-brand-textMuted text-xs mt-1">Rooms ({(roomProp * 100).toFixed(1)}%) {numOr(hours) > 0 ? `× time used (${(timeProp * 100).toFixed(1)}% of the week)` : "× full-time room use"}.</p>
            </div>
          </div>

          {/* bills */}
          <div className="bg-[#0C131D] border border-white/10 rounded-xl p-6">
            <h4 className="font-display font-semibold text-white mb-4">Your household bills</h4>
            <div className="space-y-3">
              {BILL_FIELDS.map(([k, label, note]) => (
                <div key={k}>
                  <label className={labelCls}>{label}</label>
                  <div className="flex items-center gap-3">
                    <input type="number" step="0.01" value={bills[k] || ""} onChange={setBill(k)} className={inputCls} data-testid={`home-bill-${k}`} placeholder="0.00" />
                    <span className="text-brand-cyan text-sm whitespace-nowrap w-24 text-right" data-testid={`home-claim-${k}`}>{gbp(numOr(bills[k]) * pct / 100)}</span>
                  </div>
                  {note && <p className="text-brand-textMuted text-xs mt-1">{note}</p>}
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* result + save */}
      <div className="mt-6 flex flex-wrap items-center justify-between gap-4 bg-[#0C131D] border border-white/10 rounded-xl p-6">
        <div>
          <p className="text-brand-textSec text-sm">Claimable as a business cost {method === "actual" && <span className="text-brand-textMuted">({pct.toFixed(1)}% of {gbp(billTotal)})</span>}</p>
          <p className="font-display font-bold text-3xl text-brand-cyan" data-testid="home-total-claim">{gbp(claim)}<span className="text-brand-textMuted text-base font-normal"> / year</span></p>
        </div>
        <button onClick={saveExpense} disabled={saving || claim <= 0} className="btn-primary px-5 py-2.5 text-sm disabled:opacity-50 inline-flex items-center gap-2" data-testid="home-save-expense">
          <Plus size={15} /> {saving ? "Saving…" : "Save as expense"}
        </button>
      </div>
      <p className="text-brand-textMuted text-xs mt-3">Saving adds a single "Use of Home" expense dated today, funded by you (added to what the company owes you). Adjust the date/amount afterwards in Transactions if needed.</p>
    </div>
  );
};

// ---- Director's Loan Account ----
const DirectorsLoanPanel = ({ onChange }) => {
  const [dla, setDla] = useState(null);
  const [opening, setOpening] = useState("");
  const [repDate, setRepDate] = useState("");
  const [repAmount, setRepAmount] = useState("");
  const [saving, setSaving] = useState(false);

  const load = () => api.get("/finance/dla").then((r) => { setDla(r.data); setOpening(r.data.opening_balance || 0); }).catch(() => {});
  useEffect(() => { load(); }, []);

  const saveOpening = async () => {
    try { const { data } = await api.put("/finance/dla/opening", { opening_balance: parseFloat(opening) || 0 }); setDla(data); onChange?.(); toast.success("Opening balance saved"); }
    catch { toast.error("Save failed"); }
  };
  const addRep = async (e) => {
    e.preventDefault();
    if (!repDate || !(parseFloat(repAmount) > 0)) { toast.error("Enter a date and a repayment amount."); return; }
    setSaving(true);
    try { const { data } = await api.post("/finance/dla/repayment", { date: repDate, amount: parseFloat(repAmount) }); setDla(data); setRepDate(""); setRepAmount(""); onChange?.(); toast.success("Repayment logged"); }
    catch { toast.error("Save failed"); }
    finally { setSaving(false); }
  };
  const delRep = async (id) => { if (window.confirm("Delete this repayment?")) { const { data } = await api.delete(`/finance/dla/repayment/${id}`); setDla(data); onChange?.(); toast.success("Deleted"); } };

  if (!dla) return <p className="text-brand-textMuted">Loading…</p>;

  return (
    <div data-testid="finance-dla">
      <div className="bg-amber-400/10 border border-amber-400/30 rounded-lg px-4 py-3 mb-5 text-sm text-brand-textSec">
        The <span className="text-amber-300 font-medium">director's loan</span> is what the company owes you. It builds up automatically from your owner-funded expenses and mileage claims (plus any opening balance). Log each repayment below and the outstanding balance updates instantly.
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        <StatCard label="Total Owed" value={gbp(dla.owed)} accent="text-amber-400" />
        <StatCard label="Total Repaid" value={gbp(dla.total_repaid)} accent="text-brand-cyan" />
        <div className="bg-[#0C131D] border border-brand-cyan/30 rounded-xl p-4 col-span-2" data-testid="dla-balance-card">
          <p className="text-brand-textMuted text-xs uppercase tracking-wider">Outstanding balance</p>
          <p className="font-display font-bold text-3xl mt-1 text-white" data-testid="dla-balance-value">{gbp(dla.balance)}</p>
        </div>
      </div>

      {/* Breakdown */}
      <div className="border border-white/10 rounded-lg p-4 mb-6 text-sm max-w-md">
        <div className="flex justify-between py-1"><span className="text-brand-textSec">Opening balance</span><span className="text-white/90">{gbp(dla.opening_balance)}</span></div>
        <div className="flex justify-between py-1"><span className="text-brand-textSec">Owner-funded expenses</span><span className="text-white/90">{gbp(dla.owner_expenses)}</span></div>
        <div className="flex justify-between py-1"><span className="text-brand-textSec">Mileage claims</span><span className="text-white/90">{gbp(dla.mileage_total)}</span></div>
        <div className="flex justify-between py-1.5 border-t border-white/10 mt-1"><span className="text-white">Total owed to director</span><span className="text-amber-400 font-semibold">{gbp(dla.owed)}</span></div>
        <div className="flex justify-between py-1"><span className="text-brand-textSec">Less repaid</span><span className="text-brand-cyan">− {gbp(dla.total_repaid)}</span></div>
        <div className="flex justify-between py-1.5 border-t border-white/10 mt-1"><span className="text-white font-medium">Outstanding</span><span className="text-white font-bold">{gbp(dla.balance)}</span></div>
      </div>

      {/* Opening balance editor */}
      <div className="flex flex-wrap items-end gap-3 mb-6">
        <div>
          <label className="text-sm text-brand-textSec mb-1.5 block">Opening balance (already owed before you started)</label>
          <input type="number" step="0.01" value={opening} onChange={(e) => setOpening(e.target.value)} className={`${inputCls} w-56`} data-testid="dla-opening-input" placeholder="0.00" />
        </div>
        <button onClick={saveOpening} className="btn-ghost px-4 py-2.5 text-sm" data-testid="dla-opening-save">Save opening balance</button>
      </div>

      {/* Add repayment */}
      <form onSubmit={addRep} className="flex flex-wrap items-end gap-3 mb-6 p-4 border border-white/10 rounded-lg bg-[#0C131D]">
        <div>
          <label className="text-sm text-brand-textSec mb-1.5 block">Repayment date</label>
          <input type="date" required value={repDate} onChange={(e) => setRepDate(e.target.value)} className={`${inputCls} w-44`} data-testid="dla-rep-date" />
        </div>
        <div>
          <label className="text-sm text-brand-textSec mb-1.5 block">Amount paid to director (£)</label>
          <input type="number" step="0.01" required value={repAmount} onChange={(e) => setRepAmount(e.target.value)} className={`${inputCls} w-44`} data-testid="dla-rep-amount" placeholder="0.00" />
        </div>
        <button type="submit" disabled={saving} className="btn-primary px-5 py-2.5 text-sm disabled:opacity-60 inline-flex items-center gap-2" data-testid="dla-rep-add"><Plus size={15} /> {saving ? "Saving…" : "Log repayment"}</button>
      </form>

      {/* Repayments table */}
      <div className="overflow-x-auto border border-white/10 rounded-lg" data-testid="finance-dla-table">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-white/10 text-left text-brand-textSec">
              <th className="px-4 py-3 font-medium">Date</th>
              <th className="px-4 py-3 font-medium text-right">Repayment</th>
              <th className="px-4 py-3 font-medium text-right">Balance after</th>
              <th className="px-4 py-3 font-medium"></th>
            </tr>
          </thead>
          <tbody>
            {dla.repayments.map((r) => (
              <tr key={r.id} className="border-b border-white/5 last:border-0" data-testid={`dla-rep-row-${r.id}`}>
                <td className="px-4 py-3 text-white/90 whitespace-nowrap">{r.date}</td>
                <td className="px-4 py-3 text-right text-brand-cyan font-medium whitespace-nowrap">{gbp(r.amount)}</td>
                <td className="px-4 py-3 text-right text-white/90 whitespace-nowrap">{gbp(r.balance_after)}</td>
                <td className="px-4 py-3">
                  <div className="flex justify-end">
                    <button onClick={() => delRep(r.id)} className="h-8 w-8 grid place-items-center rounded-md border border-white/10 text-brand-textSec hover:text-red-400" data-testid={`dla-rep-delete-${r.id}`}><Trash2 size={14} /></button>
                  </div>
                </td>
              </tr>
            ))}
            {dla.repayments.length === 0 && <tr><td colSpan={4} className="px-4 py-8 text-center text-brand-textMuted">No repayments logged yet.</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
};

// ---- Uber / Just Eat: statement upload with automatic reading ----
const GIG_CATEGORY = "Uber/Just Eat Contracting";

const StatementModal = ({ onClose, onSaved }) => {
  const [file, setFile] = useState(null);
  const [form, setForm] = useState({ amount: "", miles: "", date: "", description: "" });
  const [step, setStep] = useState("upload");
  const [saving, setSaving] = useState(false);

  const onPick = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setStep("reading");
    try {
      const fd = new FormData();
      fd.append("file", f);
      const { data: up } = await api.post("/finance/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setFile(up);
      try {
        const { data } = await api.post("/finance/gig/read-statement", { file_id: up.file_id });
        setForm({ amount: data.total ?? "", miles: data.miles ?? "", date: /^\d{4}-\d{2}-\d{2}$/.test(data.date) ? data.date : "", description: [data.platform !== "Unknown" ? data.platform : "", data.period].filter(Boolean).join(" — ") || "Monthly earnings" });
        toast.success(data.total != null ? "Statement read — please check the figures" : "Couldn't find the total — please enter it");
      } catch (err) {
        toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Couldn't read the statement — please enter it manually");
        setForm((s) => ({ ...s, description: "Monthly earnings" }));
      }
      setStep("review");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Upload failed");
      setStep("upload");
    }
  };

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.post("/finance/transactions", { type: "income", category: GIG_CATEGORY, gig: true, amount: parseFloat(form.amount) || 0, date: form.date, description: form.description, receipt_file_id: file.file_id, receipt_filename: file.filename });
      const miles = parseFloat(form.miles) || 0;
      if (miles > 0) await api.post("/finance/mileage", { date: form.date, miles, purpose: `${form.description || "Uber / Just Eat"} (statement)`, gig: true });
      toast.success(miles > 0 ? "Earnings and mileage saved" : "Earnings saved");
      onSaved();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm grid place-items-center p-4 overflow-y-auto" data-testid="statement-modal">
      <div className="bg-[#0C131D] border border-white/10 rounded-xl w-full max-w-lg my-8">
        <div className="flex items-center justify-between px-6 py-4 border-b border-white/10">
          <h3 className="font-display font-semibold text-white">Upload earnings statement</h3>
          <button onClick={onClose} className="text-brand-textMuted hover:text-white" data-testid="statement-modal-close"><X size={20} /></button>
        </div>
        {step !== "review" ? (
          <div className="p-6">
            <label className={`flex flex-col items-center justify-center gap-2 border border-dashed border-white/20 rounded-lg py-10 cursor-pointer hover:border-brand-cyan/60 transition-colors ${step === "reading" ? "pointer-events-none opacity-70" : ""}`}>
              <FileText size={26} className="text-brand-cyan" />
              <span className="text-white text-sm font-medium">{step === "reading" ? "Reading your statement…" : "Choose the monthly statement (PDF or screenshot)"}</span>
              <span className="text-brand-textMuted text-xs">The total is read automatically — you'll check it before saving.</span>
              <input type="file" accept="image/*,application/pdf" onChange={onPick} className="hidden" data-testid="statement-file-input" />
            </label>
          </div>
        ) : (
          <form onSubmit={save} className="p-6 space-y-4">
            <div className="grid sm:grid-cols-2 gap-4">
              <div>
                <label className="text-sm text-brand-textSec mb-1.5 block">Total earnings (£)</label>
                <input type="number" step="0.01" required value={form.amount} onChange={(e) => setForm((s) => ({ ...s, amount: e.target.value }))} className={inputCls} data-testid="statement-amount" />
              </div>
              <div>
                <label className="text-sm text-brand-textSec mb-1.5 block">Date</label>
                <input type="date" required value={form.date} onChange={(e) => setForm((s) => ({ ...s, date: e.target.value }))} className={inputCls} data-testid="statement-date" />
              </div>
            </div>
            <div>
              <label className="text-sm text-brand-textSec mb-1.5 block">Description</label>
              <input value={form.description} onChange={(e) => setForm((s) => ({ ...s, description: e.target.value }))} className={inputCls} data-testid="statement-description" />
            </div>
            <div>
              <label className="text-sm text-brand-textSec mb-1.5 block">Miles driven</label>
              <input type="number" step="0.1" min="0" value={form.miles} onChange={(e) => setForm((s) => ({ ...s, miles: e.target.value }))} className={inputCls} data-testid="statement-miles" placeholder="Leave blank if not on the statement" />
              {parseFloat(form.miles) > 0 && <p className="text-brand-textMuted text-xs mt-1.5" data-testid="statement-miles-claim">Saved as a mileage claim ≈ {gbp(parseFloat(form.miles) * 0.55)} (HMRC 55p/mile, 25p after 10,000 miles).</p>}
            </div>
            <a href={receiptUrl(file.file_id)} target="_blank" rel="noreferrer" className="text-brand-cyan text-sm inline-flex items-center gap-1" data-testid="statement-view">
              <Paperclip size={13} /> {file.filename} <ExternalLink size={13} />
            </a>
            <div className="flex gap-3 pt-1">
              <button type="submit" disabled={saving} className="btn-primary px-5 py-2.5 text-sm disabled:opacity-60" data-testid="statement-save">{saving ? "Saving…" : "Save earnings"}</button>
              <button type="button" onClick={onClose} className="btn-ghost px-5 py-2.5 text-sm">Cancel</button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
};

const StatementMilesModal = ({ initial, onClose, onSaved }) => {
  const [form, setForm] = useState({ date: initial.date || "", miles: initial.miles || "", purpose: initial.purpose || "", gig: !!initial.gig });
  const [saving, setSaving] = useState(false);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.put(`/finance/mileage/${initial.id}`, { ...form, miles: parseFloat(form.miles) || 0 });
      toast.success("Saved");
      onSaved();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Save failed");
    } finally {
      setSaving(false);
    }
  };
  return (
    <div className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm grid place-items-center p-4 overflow-y-auto" data-testid="statement-miles-modal">
      <div className="bg-[#0C131D] border border-white/10 rounded-xl w-full max-w-lg my-8">
        <div className="flex items-center justify-between px-6 py-4 border-b border-white/10">
          <h3 className="font-display font-semibold text-white">Edit statement mileage</h3>
          <button onClick={onClose} className="text-brand-textMuted hover:text-white" data-testid="statement-miles-close"><X size={20} /></button>
        </div>
        <form onSubmit={save} className="p-6 space-y-4">
          <div className="grid sm:grid-cols-2 gap-4">
            <div>
              <label className="text-sm text-brand-textSec mb-1.5 block">Date</label>
              <input type="date" required value={form.date} onChange={set("date")} className={inputCls} data-testid="statement-miles-date" />
            </div>
            <div>
              <label className="text-sm text-brand-textSec mb-1.5 block">Miles</label>
              <input type="number" step="0.1" min="0" required value={form.miles} onChange={set("miles")} className={inputCls} data-testid="statement-miles-input" />
            </div>
          </div>
          <div>
            <label className="text-sm text-brand-textSec mb-1.5 block">Description</label>
            <input value={form.purpose} onChange={set("purpose")} className={inputCls} data-testid="statement-miles-purpose" />
          </div>
          <div className="flex gap-3 pt-1">
            <button type="submit" disabled={saving} className="btn-primary px-5 py-2.5 text-sm disabled:opacity-60" data-testid="statement-miles-save">{saving ? "Saving…" : "Save"}</button>
            <button type="button" onClick={onClose} className="btn-ghost px-5 py-2.5 text-sm">Cancel</button>
          </div>
        </form>
      </div>
    </div>
  );
};

const GigRowActions = ({ onEdit, onDel, id }) => (
  <div className="flex gap-2 justify-end">
    <button onClick={onEdit} className="h-8 w-8 grid place-items-center rounded-md border border-white/10 text-brand-textSec hover:text-brand-cyan" data-testid={`gig-edit-${id}`}><Pencil size={14} /></button>
    <button onClick={onDel} className="h-8 w-8 grid place-items-center rounded-md border border-white/10 text-brand-textSec hover:text-red-400" data-testid={`gig-delete-${id}`}><Trash2 size={14} /></button>
  </div>
);

const GigTable = ({ title, testid, cols, rows, empty }) => (
  <div className="mb-6" data-testid={testid}>
    <h4 className="font-display font-semibold text-white mb-2">{title}</h4>
    <div className="overflow-x-auto border border-white/10 rounded-lg">
      <table className="w-full text-sm">
        <thead><tr className="border-b border-white/10 text-left text-brand-textSec">{cols.map((c, i) => <th key={i} className={`px-4 py-3 font-medium ${i === cols.length - 2 ? "text-right" : ""}`}>{c}</th>)}</tr></thead>
        <tbody>
          {rows}
          {rows.length === 0 && <tr><td colSpan={cols.length} className="px-4 py-6 text-center text-brand-textMuted">{empty}</td></tr>}
        </tbody>
      </table>
    </div>
  </div>
);

const GigPanel = ({ txs, mileage, onChanged, onNewExpense, onEditTx, onEditTrip, onDelTx, onDelTrip }) => {
  const [stmtOpen, setStmtOpen] = useState(false);
  const earnings = txs.filter((t) => t.type === "income" && (t.gig || t.category === GIG_CATEGORY));
  const expenses = txs.filter((t) => t.type === "expense" && t.gig);
  const trips = mileage.filter((m) => m.gig);
  const sum = (a) => a.reduce((s, x) => s + Number(x.amount || 0), 0);
  const totE = sum(earnings), totX = sum(expenses), totM = sum(trips);
  const cell = "px-4 py-3";
  return (
    <div data-testid="finance-gig-panel">
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        <StatCard label="Gig Earnings" value={gbp(totE)} accent="text-brand-cyan" />
        <StatCard label="Gig Expenses" value={gbp(totX)} accent="text-red-400" />
        <StatCard label="Gig Mileage" value={gbp(totM)} />
        <StatCard label="Gig Profit" value={gbp(totE - totX - totM)} accent={totE - totX - totM >= 0 ? "text-brand-cyan" : "text-red-400"} />
      </div>
      <div className="flex flex-wrap gap-2 mb-6">
        <button onClick={() => setStmtOpen(true)} className="btn-primary px-4 py-2 text-sm inline-flex items-center gap-2" data-testid="gig-upload-statement"><FileText size={15} /> Upload Statement</button>
        <button onClick={onNewExpense} className="btn-ghost px-4 py-2 text-sm inline-flex items-center gap-2" data-testid="gig-new-expense"><Plus size={15} /> New Expense</button>
      </div>
      <GigTable title="Monthly earnings" testid="gig-earnings-table" cols={["Date", "Description", "Statement", "Amount", ""]} empty="No statements uploaded yet."
        rows={earnings.map((t) => (
          <tr key={t.id} className="border-b border-white/5 last:border-0" data-testid={`gig-earning-row-${t.id}`}>
            <td className={`${cell} text-white/90 whitespace-nowrap`}>{t.date}</td>
            <td className={`${cell} text-white/80`}>{t.description || "—"}</td>
            <td className={cell}>{t.receipt_file_id ? <a href={receiptUrl(t.receipt_file_id)} target="_blank" rel="noreferrer" className="text-brand-cyan inline-flex items-center gap-1"><Paperclip size={13} /> View</a> : <span className="text-brand-textMuted">—</span>}</td>
            <td className={`${cell} text-right text-brand-cyan font-medium whitespace-nowrap`}>{gbp(t.amount)}</td>
            <td className={cell}><GigRowActions id={t.id} onEdit={() => onEditTx(t)} onDel={() => onDelTx(t.id)} /></td>
          </tr>
        ))} />
      <GigTable title="Expenses" testid="gig-expenses-table" cols={["Date", "Category", "Description", "Amount", ""]} empty="No Uber / Just Eat expenses yet."
        rows={expenses.map((t) => (
          <tr key={t.id} className="border-b border-white/5 last:border-0" data-testid={`gig-expense-row-${t.id}`}>
            <td className={`${cell} text-white/90 whitespace-nowrap`}>{t.date}</td>
            <td className={`${cell} text-white/80`}>{t.category || "—"}</td>
            <td className={`${cell} text-white/80`}>{t.description || "—"}</td>
            <td className={`${cell} text-right text-red-400 font-medium whitespace-nowrap`}>{gbp(t.amount)}</td>
            <td className={cell}><GigRowActions id={t.id} onEdit={() => onEditTx(t)} onDel={() => onDelTx(t.id)} /></td>
          </tr>
        ))} />
      <GigTable title="Mileage (from statements)" testid="gig-mileage-table" cols={["Date", "Description", "Miles", "Claim", ""]} empty="No mileage yet — it's added when a statement includes miles."
        rows={trips.map((m) => (
          <tr key={m.id} className="border-b border-white/5 last:border-0" data-testid={`gig-trip-row-${m.id}`}>
            <td className={`${cell} text-white/90 whitespace-nowrap`}>{m.date}</td>
            <td className={`${cell} text-white/80`}>{m.purpose || (m.from_location ? `${m.from_location} → ${m.to_location}` : "—")}</td>
            <td className={`${cell} text-white/80`}>{m.miles}</td>
            <td className={`${cell} text-right text-white font-medium whitespace-nowrap`}>{gbp(m.amount)}</td>
            <td className={cell}><GigRowActions id={m.id} onEdit={() => onEditTrip(m)} onDel={() => onDelTrip(m.id)} /></td>
          </tr>
        ))} />
      {stmtOpen && <StatementModal onClose={() => setStmtOpen(false)} onSaved={() => { setStmtOpen(false); onChanged(); }} />}
    </div>
  );
};

export const FinancePanel = () => {
  const [view, setView] = useState("transactions");
  const [taxYear, setTaxYear] = useState(currentTaxYear());
  const [summary, setSummary] = useState(null);
  const [txs, setTxs] = useState([]);
  const [mileage, setMileage] = useState([]);
  const [txModal, setTxModal] = useState(null); // {open, initial}
  const [milModal, setMilModal] = useState(null);
  const [fishModal, setFishModal] = useState(null);
  const [softwareModal, setSoftwareModal] = useState(null);
  const [missingOnly, setMissingOnly] = useState(false);

  const missingReceipts = txs.filter((t) => t.type === "expense" && !t.receipt_file_id && !t.receipt_not_required);
  const [incomeOnly, setIncomeOnly] = useState(false);
  const shownTxs = missingOnly ? missingReceipts : incomeOnly ? txs.filter((t) => t.type === "income") : txs;
  const scrollToList = () => setTimeout(() => document.querySelector('[data-testid="finance-list-anchor"]')?.scrollIntoView({ behavior: "smooth" }), 50);
  const liveFish = txs.filter((t) => t.category === "Livestock/Fish");
  const software = txs.filter((t) => t.category === "Software/Subscriptions");
  const misc = txs.filter((t) => t.category === "Misc");

  const load = useCallback(() => {
    const params = taxYear && taxYear !== "all" ? { tax_year: taxYear } : {};
    api.get("/finance/summary", { params }).then((r) => setSummary(r.data)).catch(() => {});
    api.get("/finance/transactions", { params }).then((r) => setTxs(r.data)).catch(() => {});
    api.get("/finance/mileage", { params }).then((r) => setMileage(r.data)).catch(() => {});
  }, [taxYear]);
  useEffect(load, [load]);

  const delTx = async (id) => { if (window.confirm("Delete this entry?")) { await api.delete(`/finance/transactions/${id}`); toast.success("Deleted"); load(); } };
  const delMil = async (id) => { if (window.confirm("Delete this trip?")) { await api.delete(`/finance/mileage/${id}`); toast.success("Deleted"); load(); } };

  return (
    <div data-testid="finance-panel">
      <div className="flex flex-wrap items-center justify-between gap-4 mb-6">
        <div>
          <h2 className="font-display font-bold text-2xl text-white">Finance</h2>
          <p className="text-brand-textMuted text-sm">Private expense, income & mileage tracking for your tax return.</p>
        </div>
        <div className="flex items-center gap-2">
          <label className="text-sm text-brand-textSec">Tax year</label>
          <select value={taxYear} onChange={(e) => setTaxYear(e.target.value === "all" ? "all" : Number(e.target.value))}
            className="bg-[#0C131D] border border-white/10 rounded-lg px-3.5 py-2 text-sm text-white focus:outline-none focus:ring-2 focus:ring-brand-cyan"
            data-testid="finance-tax-year">
            <option value="all" className="bg-[#0C131D]">All time</option>
            {taxYearOptions().map((y) => (
              <option key={y} value={y} className="bg-[#0C131D]">{taxYearLabel(y)}</option>
            ))}
          </select>
        </div>
      </div>

      {/* Summary */}
      {summary && (
        <div className="grid grid-cols-2 lg:grid-cols-3 gap-4 mb-8">
          <StatCard label="Income" value={gbp(summary.income)} accent="text-brand-cyan" active={view === "transactions" && incomeOnly}
            onClick={() => { setView("transactions"); setMissingOnly(false); setIncomeOnly(true); scrollToList(); }} />
          <StatCard label="Expenses" value={gbp(summary.expenses)} accent="text-red-400" />
          <StatCard label="Mileage Claim" value={gbp(summary.mileage_amount)} active={view === "mileage"}
            onClick={() => { setView("mileage"); scrollToList(); }} />
          <StatCard label="Profit" value={gbp(summary.profit)} accent={summary.profit >= 0 ? "text-brand-cyan" : "text-red-400"} />
          <StatCard label="Loan Balance" value={gbp(summary.dla_balance ?? summary.owed_to_director)} accent="text-amber-400" />
          <StatCard label="Total Claimable" value={gbp(summary.total_claimable)} accent="text-white" />
        </div>
      )}

      {/* Monthly breakdown */}
      {summary && summary.monthly && summary.monthly.length > 0 && (
        <div className="mb-8" data-testid="finance-monthly">
          <h3 className="font-display font-semibold text-white mb-3">Monthly breakdown</h3>
          <div className="overflow-x-auto border border-white/10 rounded-lg">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-white/10 text-left text-brand-textSec">
                  <th className="px-4 py-3 font-medium">Month</th>
                  <th className="px-4 py-3 font-medium text-right">Income</th>
                  <th className="px-4 py-3 font-medium text-right">Expenses</th>
                  <th className="px-4 py-3 font-medium text-right">Mileage</th>
                  <th className="px-4 py-3 font-medium text-right">Profit</th>
                </tr>
              </thead>
              <tbody>
                {summary.monthly.map((r) => (
                  <tr key={r.month} className="border-b border-white/5 last:border-0" data-testid={`monthly-row-${r.month}`}>
                    <td className="px-4 py-3 text-white/90 whitespace-nowrap">{monthLabel(r.month)}</td>
                    <td className="px-4 py-3 text-right text-brand-cyan">{gbp(r.income)}</td>
                    <td className="px-4 py-3 text-right text-red-400">{gbp(r.expenses)}</td>
                    <td className="px-4 py-3 text-right text-white/80">{gbp(r.mileage)}</td>
                    <td className={`px-4 py-3 text-right font-medium ${r.profit >= 0 ? "text-brand-cyan" : "text-red-400"}`}>{gbp(r.profit)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* view toggle + actions */}
      <div className="flex flex-wrap items-center justify-between gap-3 mb-5 scroll-mt-6" data-testid="finance-list-anchor">
        <div className="flex flex-wrap gap-2 p-1 bg-[#0C131D] rounded-full">
          {[["transactions", "Transactions"], ["livefish", "Live Fish"], ["software", "Software"], ["misc", "Misc"], ["mileage", "Mileage"], ["gig", "Uber / Just Eat"], ["dla", "Director's Loan"], ["home", "Use of Home"]].map(([v, l]) => (
            <button key={v} onClick={() => { setView(v); setIncomeOnly(false); }} data-testid={`finance-view-${v}`}
              className={`px-4 py-2 rounded-full text-sm font-medium transition-colors ${view === v ? "bg-brand-cyan text-brand-base" : "text-brand-textSec hover:text-white"}`}>
              {l}
            </button>
          ))}
        </div>
        <div className="flex gap-2">
          <a href={pdfUrl(taxYear)} className="btn-ghost px-4 py-2 text-sm inline-flex items-center gap-2" data-testid="finance-pdf">
            <FileText size={15} /> Year Summary (PDF)
          </a>
          {(view === "transactions" || view === "mileage" || view === "livefish" || view === "software" || view === "misc") && (
            <a href={csvUrl(view === "mileage" ? "mileage" : "transactions", taxYear)} className="btn-ghost px-4 py-2 text-sm inline-flex items-center gap-2" data-testid="finance-export">
              <Download size={15} /> Export CSV
            </a>
          )}
          {view === "dla" && (
            <a href={csvUrl("dla", "all")} className="btn-ghost px-4 py-2 text-sm inline-flex items-center gap-2" data-testid="finance-export-dla">
              <Download size={15} /> Export CSV
            </a>
          )}
          {view === "transactions" && (
            <button onClick={() => setTxModal({})} className="btn-primary px-4 py-2 text-sm inline-flex items-center gap-2" data-testid="finance-new-tx"><Plus size={15} /> New Entry</button>
          )}
          {view === "misc" && (
            <button onClick={() => setTxModal({ presetCategory: "Misc" })} className="btn-primary px-4 py-2 text-sm inline-flex items-center gap-2" data-testid="finance-new-misc"><Plus size={15} /> New Misc</button>
          )}
          {view === "livefish" && (
            <button onClick={() => setFishModal({})} className="btn-primary px-4 py-2 text-sm inline-flex items-center gap-2" data-testid="finance-new-livefish"><Plus size={15} /> New Live Fish</button>
          )}
          {view === "software" && (
            <button onClick={() => setSoftwareModal({})} className="btn-primary px-4 py-2 text-sm inline-flex items-center gap-2" data-testid="finance-new-software"><Plus size={15} /> New Software</button>
          )}
          {view === "mileage" && (
            <button onClick={() => setMilModal({})} className="btn-primary px-4 py-2 text-sm inline-flex items-center gap-2" data-testid="finance-new-mileage"><Plus size={15} /> New Trip</button>
          )}
        </div>
      </div>

      {/* Receipt reminder banner (shown on any finance sub-view) */}
      {missingReceipts.length > 0 && (
        <div className="flex flex-wrap items-center justify-between gap-3 mb-5 bg-amber-400/10 border border-amber-400/30 rounded-lg px-4 py-3" data-testid="finance-receipt-warning">
          <p className="text-amber-300 text-sm inline-flex items-center gap-2">
            <AlertTriangle size={16} />
            <span><strong>{missingReceipts.length}</strong> {missingReceipts.length === 1 ? "expense is" : "expenses are"} missing a receipt.</span>
          </p>
          {view === "transactions" ? (
            <button
              onClick={() => setMissingOnly((v) => !v)}
              data-testid="finance-missing-filter"
              className={`px-3.5 py-1.5 rounded-full text-xs font-medium transition-colors ${missingOnly ? "bg-amber-400 text-brand-base" : "border border-amber-400/40 text-amber-300 hover:bg-amber-400/10"}`}
            >
              {missingOnly ? "Showing missing only — show all" : "Show missing receipts only"}
            </button>
          ) : (
            <button
              onClick={() => { setView("transactions"); setMissingOnly(true); }}
              data-testid="finance-missing-review"
              className="px-3.5 py-1.5 rounded-full text-xs font-medium border border-amber-400/40 text-amber-300 hover:bg-amber-400/10 transition-colors"
            >
              Review missing receipts
            </button>
          )}
        </div>
      )}

      {view === "transactions" && incomeOnly && !missingOnly && (
        <div className="flex items-center justify-between gap-3 mb-3 bg-brand-cyan/10 border border-brand-cyan/30 rounded-lg px-4 py-2.5" data-testid="finance-income-filter-banner">
          <span className="text-brand-cyan text-sm">Showing income only</span>
          <button onClick={() => setIncomeOnly(false)} className="px-3.5 py-1.5 rounded-full text-xs font-medium border border-brand-cyan/40 text-brand-cyan hover:bg-brand-cyan/10" data-testid="finance-income-filter-clear">Show all</button>
        </div>
      )}

      {/* Transactions table */}
      {view === "transactions" && (
        <div className="overflow-x-auto border border-white/10 rounded-lg" data-testid="finance-tx-table">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-white/10 text-left text-brand-textSec">
                <th className="px-4 py-3 font-medium">Date</th>
                <th className="px-4 py-3 font-medium">Category</th>
                <th className="px-4 py-3 font-medium">Description</th>
                <th className="px-4 py-3 font-medium">Funded by</th>
                <th className="px-4 py-3 font-medium text-right">Amount</th>
                <th className="px-4 py-3 font-medium">Receipt</th>
                <th className="px-4 py-3 font-medium"></th>
              </tr>
            </thead>
            <tbody>
              {shownTxs.map((t) => (
                <tr key={t.id} className="border-b border-white/5 last:border-0" data-testid={`tx-row-${t.id}`}>
                  <td className="px-4 py-3 text-white/90 whitespace-nowrap">{t.date}</td>
                  <td className="px-4 py-3 text-white/80">{t.category || "—"}</td>
                  <td className="px-4 py-3 text-white/80">{t.description || "—"}</td>
                  <td className="px-4 py-3">
                    {t.type === "expense" ? (
                      <span className={`inline-block px-2 py-0.5 rounded-full text-xs font-medium ${(t.funded_by || "owner") === "owner" ? "bg-amber-400/15 text-amber-400" : "bg-brand-cyan/15 text-brand-cyan"}`} data-testid={`tx-funded-badge-${t.id}`}>
                        {(t.funded_by || "owner") === "owner" ? "Owner" : "Company"}
                      </span>
                    ) : <span className="text-brand-textMuted">—</span>}
                  </td>
                  <td className={`px-4 py-3 text-right font-medium whitespace-nowrap ${t.type === "income" ? "text-brand-cyan" : "text-red-400"}`}>
                    {t.type === "income" ? "+" : "−"}{gbp(t.amount)}
                    <OrigHint t={t} />
                  </td>
                  <td className="px-4 py-3">
                    {t.receipt_file_id ? (
                      <a href={receiptUrl(t.receipt_file_id)} target="_blank" rel="noreferrer" className="text-brand-cyan inline-flex items-center gap-1" data-testid={`tx-receipt-link-${t.id}`}>
                        <Paperclip size={13} /> View
                      </a>
                    ) : t.receipt_not_required ? (
                      <span className="text-brand-textMuted text-xs" data-testid={`tx-receipt-na-${t.id}`}>Not required</span>
                    ) : t.type === "expense" ? (
                      <span className="text-amber-400 inline-flex items-center gap-1" title="No receipt attached" data-testid={`tx-receipt-missing-${t.id}`}>
                        <AlertTriangle size={13} /> Missing
                      </span>
                    ) : <span className="text-brand-textMuted">—</span>}
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex gap-2 justify-end">
                      <button onClick={() => setTxModal(t)} className="h-8 w-8 grid place-items-center rounded-md border border-white/10 text-brand-textSec hover:text-brand-cyan" data-testid={`tx-edit-${t.id}`}><Pencil size={14} /></button>
                      <button onClick={() => delTx(t.id)} className="h-8 w-8 grid place-items-center rounded-md border border-white/10 text-brand-textSec hover:text-red-400" data-testid={`tx-delete-${t.id}`}><Trash2 size={14} /></button>
                    </div>
                  </td>
                </tr>
              ))}
              {shownTxs.length === 0 && <tr><td colSpan={7} className="px-4 py-8 text-center text-brand-textMuted">{missingOnly ? "No expenses are missing a receipt — nice work." : incomeOnly ? "No income logged for this period." : "No entries yet. Add your first expense or income."}</td></tr>}
            </tbody>
          </table>
        </div>
      )}

      {/* Mileage table */}
      {view === "mileage" && (
        <div className="overflow-x-auto border border-white/10 rounded-lg" data-testid="finance-mileage-table">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-white/10 text-left text-brand-textSec">
                <th className="px-4 py-3 font-medium">Date</th>
                <th className="px-4 py-3 font-medium">From → To</th>
                <th className="px-4 py-3 font-medium">Purpose</th>
                <th className="px-4 py-3 font-medium text-right">Miles</th>
                <th className="px-4 py-3 font-medium text-right">Claim</th>
                <th className="px-4 py-3 font-medium"></th>
              </tr>
            </thead>
            <tbody>
              {mileage.map((m) => (
                <tr key={m.id} className="border-b border-white/5 last:border-0" data-testid={`mileage-row-${m.id}`}>
                  <td className="px-4 py-3 text-white/90 whitespace-nowrap">{m.date}</td>
                  <td className="px-4 py-3 text-white/80">{m.from_location ? <>{m.from_location} → {m.to_location}</> : <span className="text-brand-textMuted">From statement</span>}{m.is_return ? <span className="text-brand-textMuted text-xs"> · return</span> : null}</td>
                  <td className="px-4 py-3 text-white/80">{m.purpose || "—"}</td>
                  <td className="px-4 py-3 text-right text-white/90">{m.miles}</td>
                  <td className="px-4 py-3 text-right text-brand-cyan font-medium whitespace-nowrap">{gbp(m.amount)}</td>
                  <td className="px-4 py-3">
                    <div className="flex gap-2 justify-end">
                      <button onClick={() => setMilModal(m)} className="h-8 w-8 grid place-items-center rounded-md border border-white/10 text-brand-textSec hover:text-brand-cyan" data-testid={`mileage-edit-${m.id}`}><Pencil size={14} /></button>
                      <button onClick={() => delMil(m.id)} className="h-8 w-8 grid place-items-center rounded-md border border-white/10 text-brand-textSec hover:text-red-400" data-testid={`mileage-delete-${m.id}`}><Trash2 size={14} /></button>
                    </div>
                  </td>
                </tr>
              ))}
              {mileage.length === 0 && <tr><td colSpan={6} className="px-4 py-8 text-center text-brand-textMuted">No trips logged yet.</td></tr>}
            </tbody>
          </table>
        </div>
      )}

      {/* Live Fish table */}
      {view === "livefish" && (
        <div data-testid="finance-livefish">
          <div className="bg-brand-cyan/10 border border-brand-cyan/25 rounded-lg px-4 py-3 mb-5 text-sm text-brand-textSec flex items-start gap-2">
            <Fish size={16} className="text-brand-cyan mt-0.5 shrink-0" />
            <span>Live fish bought for the channel are a <span className="text-brand-cyan font-medium">100% allowable business cost</span> — the full purchase price is claimed. Log each buy here with the species and keep the receipt.</span>
          </div>
          <div className="overflow-x-auto border border-white/10 rounded-lg" data-testid="finance-livefish-table">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-white/10 text-left text-brand-textSec">
                  <th className="px-4 py-3 font-medium">Date</th>
                  <th className="px-4 py-3 font-medium">Species</th>
                  <th className="px-4 py-3 font-medium text-right">Qty</th>
                  <th className="px-4 py-3 font-medium">Source</th>
                  <th className="px-4 py-3 font-medium text-right">Cost</th>
                  <th className="px-4 py-3 font-medium">Receipt</th>
                  <th className="px-4 py-3 font-medium"></th>
                </tr>
              </thead>
              <tbody>
                {liveFish.map((t) => (
                  <tr key={t.id} className="border-b border-white/5 last:border-0" data-testid={`livefish-row-${t.id}`}>
                    <td className="px-4 py-3 text-white/90 whitespace-nowrap">{t.date}</td>
                    <td className="px-4 py-3 text-white/90">{t.species || t.description || "—"}</td>
                    <td className="px-4 py-3 text-right text-white/80">{t.quantity || "—"}</td>
                    <td className="px-4 py-3 text-white/80">{t.vendor || "—"}</td>
                    <td className="px-4 py-3 text-right text-red-400 font-medium whitespace-nowrap">{gbp(t.amount)}</td>
                    <td className="px-4 py-3">
                      {t.receipt_file_id ? (
                        <a href={receiptUrl(t.receipt_file_id)} target="_blank" rel="noreferrer" className="text-brand-cyan inline-flex items-center gap-1" data-testid={`livefish-receipt-link-${t.id}`}>
                          <Paperclip size={13} /> View
                        </a>
                      ) : t.receipt_not_required ? (
                        <span className="text-brand-textMuted text-xs" data-testid={`livefish-receipt-na-${t.id}`}>Not required</span>
                      ) : (
                        <span className="text-amber-400 inline-flex items-center gap-1" title="No receipt attached" data-testid={`livefish-receipt-missing-${t.id}`}>
                          <AlertTriangle size={13} /> Missing
                        </span>
                      )}
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex gap-2 justify-end">
                        <button onClick={() => setFishModal(t)} className="h-8 w-8 grid place-items-center rounded-md border border-white/10 text-brand-textSec hover:text-brand-cyan" data-testid={`livefish-edit-${t.id}`}><Pencil size={14} /></button>
                        <button onClick={() => delTx(t.id)} className="h-8 w-8 grid place-items-center rounded-md border border-white/10 text-brand-textSec hover:text-red-400" data-testid={`livefish-delete-${t.id}`}><Trash2 size={14} /></button>
                      </div>
                    </td>
                  </tr>
                ))}
                {liveFish.length === 0 && <tr><td colSpan={7} className="px-4 py-8 text-center text-brand-textMuted">No live fish logged yet. Tap "New Live Fish" to add your first purchase.</td></tr>}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Software table */}
      {view === "software" && (
        <div data-testid="finance-software">
          <div className="bg-brand-cyan/10 border border-brand-cyan/25 rounded-lg px-4 py-3 mb-5 text-sm text-brand-textSec flex items-start gap-2">
            <Monitor size={16} className="text-brand-cyan mt-0.5 shrink-0" />
            <span>Software &amp; subscriptions you use to run the channel/business — editing tools, website hosting (Emergent), AI assistants (Claude Pro) — are an <span className="text-brand-cyan font-medium">allowable business cost</span>. Log each payment and keep the invoice.</span>
          </div>
          <div className="overflow-x-auto border border-white/10 rounded-lg" data-testid="finance-software-table">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-white/10 text-left text-brand-textSec">
                  <th className="px-4 py-3 font-medium">Date</th>
                  <th className="px-4 py-3 font-medium">Service / tool</th>
                  <th className="px-4 py-3 font-medium">Billing</th>
                  <th className="px-4 py-3 font-medium">Funded by</th>
                  <th className="px-4 py-3 font-medium text-right">Cost</th>
                  <th className="px-4 py-3 font-medium">Receipt</th>
                  <th className="px-4 py-3 font-medium"></th>
                </tr>
              </thead>
              <tbody>
                {software.map((t) => (
                  <tr key={t.id} className="border-b border-white/5 last:border-0" data-testid={`software-row-${t.id}`}>
                    <td className="px-4 py-3 text-white/90 whitespace-nowrap">{t.date}</td>
                    <td className="px-4 py-3 text-white/90">{t.description || "—"}</td>
                    <td className="px-4 py-3 text-white/80">{t.billing_cycle || "—"}</td>
                    <td className="px-4 py-3">
                      <span className={`inline-block px-2 py-0.5 rounded-full text-xs font-medium ${(t.funded_by || "owner") === "owner" ? "bg-amber-400/15 text-amber-400" : "bg-brand-cyan/15 text-brand-cyan"}`}>
                        {(t.funded_by || "owner") === "owner" ? "Owner" : "Company"}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-right text-red-400 font-medium whitespace-nowrap">{gbp(t.amount)}<OrigHint t={t} /></td>
                    <td className="px-4 py-3">
                      {t.receipt_file_id ? (
                        <a href={receiptUrl(t.receipt_file_id)} target="_blank" rel="noreferrer" className="text-brand-cyan inline-flex items-center gap-1" data-testid={`software-receipt-link-${t.id}`}>
                          <Paperclip size={13} /> View
                        </a>
                      ) : t.receipt_not_required ? (
                        <span className="text-brand-textMuted text-xs" data-testid={`software-receipt-na-${t.id}`}>Not required</span>
                      ) : (
                        <span className="text-amber-400 inline-flex items-center gap-1" title="No receipt attached" data-testid={`software-receipt-missing-${t.id}`}>
                          <AlertTriangle size={13} /> Missing
                        </span>
                      )}
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex gap-2 justify-end">
                        <button onClick={() => setSoftwareModal(t)} className="h-8 w-8 grid place-items-center rounded-md border border-white/10 text-brand-textSec hover:text-brand-cyan" data-testid={`software-edit-${t.id}`}><Pencil size={14} /></button>
                        <button onClick={() => delTx(t.id)} className="h-8 w-8 grid place-items-center rounded-md border border-white/10 text-brand-textSec hover:text-red-400" data-testid={`software-delete-${t.id}`}><Trash2 size={14} /></button>
                      </div>
                    </td>
                  </tr>
                ))}
                {software.length === 0 && <tr><td colSpan={7} className="px-4 py-8 text-center text-brand-textMuted">No software logged yet. Tap "New Software" to add Claude Pro, Emergent, etc.</td></tr>}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Misc table */}
      {view === "misc" && (
        <div data-testid="finance-misc">
          <div className="bg-white/5 border border-white/10 rounded-lg px-4 py-3 mb-5 text-sm text-brand-textSec">
            Anything that doesn't fit the other sections — sundries, one-off bits, small business costs. Each is logged as a "Misc" expense and counts towards your totals.
          </div>
          <div className="overflow-x-auto border border-white/10 rounded-lg" data-testid="finance-misc-table">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-white/10 text-left text-brand-textSec">
                  <th className="px-4 py-3 font-medium">Date</th>
                  <th className="px-4 py-3 font-medium">Description</th>
                  <th className="px-4 py-3 font-medium">Funded by</th>
                  <th className="px-4 py-3 font-medium text-right">Amount</th>
                  <th className="px-4 py-3 font-medium">Receipt</th>
                  <th className="px-4 py-3 font-medium"></th>
                </tr>
              </thead>
              <tbody>
                {misc.map((t) => (
                  <tr key={t.id} className="border-b border-white/5 last:border-0" data-testid={`misc-row-${t.id}`}>
                    <td className="px-4 py-3 text-white/90 whitespace-nowrap">{t.date}</td>
                    <td className="px-4 py-3 text-white/90">{t.description || "—"}</td>
                    <td className="px-4 py-3">
                      <span className={`inline-block px-2 py-0.5 rounded-full text-xs font-medium ${(t.funded_by || "owner") === "owner" ? "bg-amber-400/15 text-amber-400" : "bg-brand-cyan/15 text-brand-cyan"}`}>
                        {(t.funded_by || "owner") === "owner" ? "Owner" : "Company"}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-right text-red-400 font-medium whitespace-nowrap">{gbp(t.amount)}</td>
                    <td className="px-4 py-3">
                      {t.receipt_file_id ? (
                        <a href={receiptUrl(t.receipt_file_id)} target="_blank" rel="noreferrer" className="text-brand-cyan inline-flex items-center gap-1" data-testid={`misc-receipt-link-${t.id}`}>
                          <Paperclip size={13} /> View
                        </a>
                      ) : t.receipt_not_required ? (
                        <span className="text-brand-textMuted text-xs" data-testid={`misc-receipt-na-${t.id}`}>Not required</span>
                      ) : (
                        <span className="text-amber-400 inline-flex items-center gap-1" title="No receipt attached" data-testid={`misc-receipt-missing-${t.id}`}>
                          <AlertTriangle size={13} /> Missing
                        </span>
                      )}
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex gap-2 justify-end">
                        <button onClick={() => setTxModal(t)} className="h-8 w-8 grid place-items-center rounded-md border border-white/10 text-brand-textSec hover:text-brand-cyan" data-testid={`misc-edit-${t.id}`}><Pencil size={14} /></button>
                        <button onClick={() => delTx(t.id)} className="h-8 w-8 grid place-items-center rounded-md border border-white/10 text-brand-textSec hover:text-red-400" data-testid={`misc-delete-${t.id}`}><Trash2 size={14} /></button>
                      </div>
                    </td>
                  </tr>
                ))}
                {misc.length === 0 && <tr><td colSpan={6} className="px-4 py-8 text-center text-brand-textMuted">No miscellaneous expenses yet. Tap "New Misc" to add one.</td></tr>}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {view === "gig" && (
        <GigPanel txs={txs} mileage={mileage} onChanged={load}
          onNewExpense={() => setTxModal({ presetCategory: "Vehicle Insurance & Repairs", presetGig: true })}
          onEditTx={(t) => setTxModal(t)} onEditTrip={(m) => setMilModal(m)} onDelTx={delTx} onDelTrip={delMil} />
      )}

      {/* Director's Loan Account */}
      {view === "dla" && <DirectorsLoanPanel onChange={load} />}

      {/* Use of Home calculator */}
      {view === "home" && <UseOfHomePanel onSaved={() => { setView("transactions"); load(); }} />}

      {txModal && <TxModal initial={txModal.id ? txModal : null} preset={txModal.id ? null : { category: txModal.presetCategory || "", gig: !!txModal.presetGig }} onClose={() => setTxModal(null)} onSaved={() => { setTxModal(null); load(); }} />}
      {milModal && milModal.id && !milModal.from_postcode && <StatementMilesModal initial={milModal} onClose={() => setMilModal(null)} onSaved={() => { setMilModal(null); load(); }} />}
      {milModal && !(milModal.id && !milModal.from_postcode) && <MileageModal initial={milModal.id ? milModal : null} preset={milModal.id ? null : { gig: !!milModal.presetGig }} onClose={() => setMilModal(null)} onSaved={() => { setMilModal(null); load(); }} />}
      {fishModal && <LiveFishModal initial={fishModal.id ? fishModal : null} onClose={() => setFishModal(null)} onSaved={() => { setFishModal(null); load(); }} />}
      {softwareModal && <SoftwareModal initial={softwareModal.id ? softwareModal : null} onClose={() => setSoftwareModal(null)} onSaved={() => { setSoftwareModal(null); load(); }} />}
    </div>
  );
};
